import urls from '@/navigation/urls'
import DateButtonGroup from '@/pages/Calendar/DateButtonGroup'
import useMaintenancePlans from '@/pages/Calendar/hooks/useMaintenancePlans'
import FileChartLineIcon from '@/assets/icons/fileChartLine.svg?react'
import { useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import useReportCtrl from '../../../MyOrganizationUsers/components/OrganizationUsersReportView/hooks/useReportCtrl'
import useReportLayout from '../../../MyOrganizationUsers/components/OrganizationUsersReportView/hooks/useReportLayout'
import useReportTableDownloadCtrl from '../../../MyOrganizationUsers/components/OrganizationUsersReportView/hooks/useReportTableDownloadCtrl'
import ReportTableContent from '../../../MyOrganizationUsers/components/ReportTableContent'
import ReportTableHeader from '../../../MyOrganizationUsers/components/ReportTableHeader'
import ReportIntervalModal from '../../../MyOrganizationUsers/components/OrganizationUsersReportView/ReportIntervalModal'
import Button from '@/shared/Buttons/Button'
import { IUser } from '@/pages/Users/users.types'
import ReportWidget from '../../../MyOrganizationUsers/components/ReportWidget'
import useReportWidgetsCtrl from '../../../MyOrganizationUsers/hooks/useReportWidgetsCtrl'
import css from './individualReport.module.css'
import cn from '@/utils/cn'

interface IProps {
  user: IUser
  loading: boolean
}

const IndividualReport = (props: IProps) => {
  const tableContentRowsRef = useRef<HTMLDivElement>(null)
  const layoutCtrl = useReportLayout(tableContentRowsRef, 1, 1800)
  const ctrl = useReportCtrl(layoutCtrl.viewingMode)
  const maintenancePlans = useMaintenancePlans(ctrl.data.filters, props.user ? [props.user] : [])
  const navigate = useNavigate()
  const reportDownloadCtrl = useReportTableDownloadCtrl(ctrl.data.filters, props.user?.id)
  const widgetsCtrl = useReportWidgetsCtrl(maintenancePlans.events)
  const [reportModalOpen, setReportModalOpen] = useState(false)

  return (
    <div className={cn('flex-lines gap16', css.wrapper)}>
      <div className="flex-space-between">
        <div className="inline-flex-gap gap16 center">
          <DateButtonGroup
            viewingMode="range"
            onDateChange={(dates) => {
              maintenancePlans.resetLoadState()
              ctrl.handlers.handleDateChange(dates)
            }}
            selectedDates={ctrl.data.filters}
            selectedSection="week"
          />
          <Button
            size="default"
            type="default"
            onClick={() => {
              maintenancePlans.resetLoadState()
              ctrl.handlers.handleTodayClick()
            }}
            text="Сегодня"
          />
        </div>
        <Button
          loading={reportDownloadCtrl.loading}
          size="default"
          type="default"
          onClick={() => setReportModalOpen(true)}
          icon={<FileChartLineIcon width={16} height={16} />}
          text="Сформировать отчет"
        />
      </div>
      <div className={css.widgetsWrapper}>
        <ReportWidget
          title="ТО выполнено"
          value={widgetsCtrl.completedCountByType}
          loading={props.loading || !maintenancePlans.isLoaded}
        />
        <ReportWidget
          title="Просрочено"
          value={widgetsCtrl.overdueCountByType}
          loading={props.loading || !maintenancePlans.isLoaded}
        />
        <ReportWidget
          title="Внеплановых ТО"
          value={maintenancePlans.events.unplanned.length}
          loading={props.loading || !maintenancePlans.isLoaded}
        />
      </div>
      <div className="flex-lines">
        <ReportTableHeader
          days={ctrl.data.daysRange}
          scrollWidth={layoutCtrl.scrollbarWidth}
          loading={props.loading || !maintenancePlans.isLoaded}
          hoverCol={ctrl.data.hoverCol}
          onHoverColChange={ctrl.handlers.handleHoverColChange}
        />
        <ReportTableContent
          days={ctrl.data.daysRange}
          ref={tableContentRowsRef}
          events={maintenancePlans.events}
          loading={props.loading || !maintenancePlans.isLoaded}
          users={[props.user]}
          hoverCol={ctrl.data.hoverCol}
          onHoverColChange={ctrl.handlers.handleHoverColChange}
          onUserCellClick={(user) => navigate(urls.employePage(user.id))}
          headerRowExist={false}
          totalRowExist={false}
          renderUsersCol={false}
        />
      </div>

      <ReportIntervalModal
        open={reportModalOpen}
        loading={reportDownloadCtrl.loading}
        onClose={() => setReportModalOpen(false)}
        onSubmit={async (interval) => {
          const isDownloaded = await reportDownloadCtrl.downloadMaintenanceReportXlsx(interval)
          if (isDownloaded) setReportModalOpen(false)
        }}
      />
    </div>
  )
}

export default IndividualReport
